import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Map, FileCheck2, Sparkles, ShieldCheck, Check, Users, Briefcase, Sprout, Wallet, Lock, CreditCard, Download, Clock, Building2, MapPin } from "lucide-react";
import { Logo } from "@/components/Common";
import { Testimonials } from "@/components/Testimonials";
import { useSeo } from "@/lib/seo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

function startSignup() {
  window.location.assign("/signup");
}
function startLogin() {
  window.location.assign("/login");
}

const ASSURANCES = [
  { icon: ShieldCheck, t: "Optional AI, you stay the author" },
  { icon: FileCheck2, t: "You own and can export every asset" },
  { icon: CreditCard, t: "Free during early access, no card" },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  useSeo({
    title: "Launch to Income | Build Your Business Step by Step",
    description: "Turn your business idea into a validated offer and launch plan through one guided workspace with practical AI support. Free during early access.",
    path: "/",
  });

  useEffect(() => {
    api.get("/roadmap").then(({ data }) => setTasks(data.tasks)).catch(() => {});
  }, []);

  const goApp = () => navigate(user?.role === "founder" ? "/founder" : user?.onboarding_complete ? "/dashboard" : "/onboarding");

  return (
    <div className="min-h-screen bg-[#FDFCFE]">
      <header className="mx-auto flex max-w-6xl items-center justify-between gap-6 px-6 py-5">
        <Logo height="h-9 sm:h-10" className="shrink-0" />
        <div className="flex items-center gap-4 text-sm text-slate-600 sm:gap-6">
          <a href="#how" className="hidden min-h-[44px] items-center px-1 hover:text-slate-900 md:inline-flex">How it works</a>
          <a href="#roadmap" className="hidden min-h-[44px] items-center px-1 hover:text-slate-900 md:inline-flex">Roadmap</a>
          {user ? (
            <button data-testid="go-to-app-btn" onClick={goApp} className="inline-flex min-h-[44px] items-center rounded-lg bg-brand px-4 py-2 font-medium text-white transition-all duration-200 hover:bg-brand-hover">
              Open workspace
            </button>
          ) : (
            <>
              <button data-testid="header-login-btn" onClick={startLogin} className="inline-flex min-h-[44px] items-center rounded-lg px-3 py-2 hover:text-slate-900">Log in</button>
              <button data-testid="header-start-btn" onClick={startSignup} className="hidden min-h-[44px] items-center rounded-lg bg-brand px-4 py-2 font-medium text-white transition-all duration-200 hover:bg-brand-hover sm:inline-flex">
                Create my free workspace
              </button>
            </>
          )}
        </div>
      </header>

      <section className="mx-auto grid max-w-6xl items-center gap-12 px-6 pb-8 pt-10 lg:grid-cols-2 lg:pt-16">
        <div className="ltifade">
          <span className="inline-block rounded-full bg-brand-surface px-3 py-1 text-xs font-medium text-brand-text">
            A workspace, not a course
          </span>
          <h1 className="mt-5 font-heading text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
            From business idea to first paying customer
          </h1>
          <p className="mt-5 max-w-lg text-lg leading-relaxed text-slate-600">
            Build the validated offer, launch plan, and assets it takes to get your first paying customer.
            Seven clear steps, optional AI drafting, and you keep the final say on everything you make.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-4">
            <button data-testid="hero-start-btn" onClick={user ? goApp : startSignup} className="inline-flex items-center gap-2 rounded-lg bg-brand px-6 py-3 font-medium text-white transition-all duration-200 hover:bg-brand-hover hover:shadow-lift">
              {user ? "Open workspace" : "Create my free workspace"} <ArrowRight size={17} />
            </button>
            <a href="#roadmap" className="inline-flex min-h-[44px] items-center text-sm font-medium text-slate-600 hover:text-slate-900">Preview the seven steps</a>
          </div>
          <div className="mt-7 flex flex-col gap-2.5">
            {ASSURANCES.map((a) => (
              <div key={a.t} className="flex items-center gap-2.5 text-sm text-slate-600">
                <a.icon size={16} className="text-brand" strokeWidth={1.9} /> {a.t}
              </div>
            ))}
          </div>
        </div>

        {/* product preview: real workspace screenshot */}
        <div className="ltifade relative">
          <div className="overflow-hidden rounded-2xl border border-brand-line bg-white shadow-lift">
            <div className="flex items-center gap-1.5 border-b border-brand-line bg-[#FDFCFE] px-4 py-2.5">
              <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
              <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
              <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
              <span className="ml-2 text-xs font-medium text-slate-400">Your Launch to Income workspace</span>
            </div>
            <img
              src="/workspace_preview.png"
              alt="A real Launch to Income workspace showing today's focus, business and launch readiness, and the seven step roadmap with completed steps"
              className="block w-full"
              width="1400"
              height="880"
              loading="lazy"
            />
          </div>
          <span className="absolute -bottom-3 left-4 inline-flex items-center gap-1.5 rounded-full border border-brand-line bg-white px-3 py-1.5 text-xs font-semibold text-brand-text shadow-soft">
            <Sparkles size={13} className="text-brand" /> A real workspace, not a mockup
          </span>
        </div>
      </section>

      <section id="roadmap" className="mx-auto max-w-6xl px-6 pt-14 pb-10 scroll-mt-20">
        <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Your seven steps from idea to first sale</h2>
        <p className="mt-2 max-w-xl text-slate-600">Each step tells you exactly what to work on, and leaves you with a finished asset you can actually use.</p>
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {tasks.map((t, i) => {
            const last = i === tasks.length - 1;
            return (
            <div key={t.slug} className={`flex items-start gap-3 rounded-xl border border-brand-line p-4 shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lift ${last ? "bg-brand-surface/60 sm:col-span-2 lg:col-span-3" : "bg-white"}`}>
              <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-brand-surface text-sm font-semibold text-brand-text">{t.order}</span>
              <div>
                <p className="font-medium">{t.title}</p>
                {t.est_minutes ? <p className="mt-0.5 inline-flex items-center gap-1 text-xs text-slate-400"><Clock size={12} /> about {t.est_minutes} min</p> : null}
                <p className="mt-1 text-sm leading-relaxed text-slate-600">{t.summary}</p>
                <p className="mt-1.5 inline-flex items-center gap-1 text-xs text-brand-text"><Check size={13} /> You will create: {t.asset_name}</p>
              </div>
            </div>
            );
          })}
        </div>
      </section>

      {/* Built for */}
      <section className="mx-auto max-w-6xl px-6 py-12">
        <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Built for people with an idea but no clear path</h2>
        <p className="mt-2 max-w-xl text-slate-700">First time starting out, building around a full time job, or picking up an idea you set down a while ago, Launch to Income tells you what to work on next.</p>
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {[
            { icon: Briefcase, title: "The working founder", body: "You have a full-time job and an idea you keep coming back to. You need a plan that fits around your week." },
            { icon: Sprout, title: "The first-time founder", body: "You've never built a business and don't know step one. You want to be told exactly what to do next." },
            { icon: Users, title: "The founder ready to restart", body: "You've bought courses and taken notes, but never shipped. This time you finish, holding real assets." },
          ].map((p) => (
            <div key={p.title} className="rounded-2xl border border-brand-line bg-white p-6 shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lift">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-surface"><p.icon size={20} className="text-brand" strokeWidth={1.8} /></span>
              <p className="mt-4 font-heading text-lg font-semibold">{p.title}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* What you get when you start (free offer), aka How it works */}
      <section id="how" className="mx-auto max-w-6xl px-6 py-6 scroll-mt-20">
        <div className="grid items-center gap-8 rounded-3xl border border-brand-line bg-white p-8 shadow-soft lg:grid-cols-2 lg:p-10">
          <div>
            <span className="inline-block rounded-full bg-brand-surface px-3 py-1 text-xs font-semibold text-brand-text">How it works</span>
            <h2 className="mt-4 font-heading text-2xl font-bold tracking-tight sm:text-3xl">A full workspace, free to start</h2>
            <p className="mt-3 text-slate-700">Select "Create my free workspace" and start step one right away. No credit card, no sales call.</p>
          </div>
          <div className="grid gap-3">
            {[
              { icon: Map, t: "A roadmap that tells you what to work on next" },
              { icon: Sparkles, t: "AI that drafts the hard parts, with you making the final call" },
              { icon: FileCheck2, t: "Real business assets you build, own, and can export any time" },
              { icon: CreditCard, t: "No card required. Free during early access." },
            ].map((r) => (
              <div key={r.t} className="flex items-start gap-3 rounded-xl border border-brand-line bg-[#FDFCFE] p-4">
                <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand-surface"><r.icon size={16} className="text-brand" strokeWidth={1.9} /></span>
                <p className="text-sm font-medium text-slate-700">{r.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Credibility / honest social proof band */}
      <section className="mt-10 bg-brand-deep py-16">
        <div className="mx-auto max-w-4xl px-6">
          <img src="/logo_white.png" alt="Launch to Income" className="h-8 w-auto" />
          <h2 className="mt-6 font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">Why we built this</h2>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-brand-mutedHover">
            Most people don't stall for lack of effort. They stall because "start a business" is really
            hundreds of small decisions in no clear order. We built Launch to Income to take out the
            guesswork: tell you what to do next, let you keep the real work you make, and give you straight
            answers along the way.
          </p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: CreditCard, t: "No card to start" },
              { icon: FileCheck2, t: "You own everything you make" },
              { icon: Sparkles, t: "AI assists, you decide" },
              { icon: Download, t: "Export your work any time" },
            ].map((m) => (
              <div key={m.t} className="flex items-center gap-2.5 rounded-xl bg-white/5 px-4 py-3">
                <m.icon size={17} className="text-brand-muted" strokeWidth={1.9} />
                <span className="text-sm font-medium text-white">{m.t}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 rounded-xl border border-white/10 bg-white/5 px-5 py-4 text-sm leading-relaxed text-brand-mutedHover" data-testid="early-access-note">
            <span className="mb-1 block font-semibold text-white">Currently in early access</span>
            We are working directly with early users to improve the guided experience. Explore the complete
            product now, create your first asset, and share feedback directly with our team.
          </p>

          <div className="mt-4 flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="founder-credibility">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-white/10">
              <Building2 size={22} className="text-white" strokeWidth={1.8} />
            </span>
            <div>
              <p className="font-heading text-base font-semibold text-white">Built and operated by AXON MEDIA LLC</p>
              <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-brand-mutedHover">
                <MapPin size={14} className="text-brand-muted" /> Charlotte, North Carolina. A small team that thinks starting a business should feel a lot less confusing than it usually does.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Testimonials />

      {/* Early access / pricing transparency */}
      <section className="mx-auto max-w-6xl px-6 py-16">
        <div className="rounded-3xl border border-brand-line bg-white p-8 shadow-soft lg:p-10">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="max-w-xl">
              <span className="inline-block rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">Early access</span>
              <h2 className="mt-4 font-heading text-2xl font-bold tracking-tight sm:text-3xl">Build your first business assets free during early access</h2>
              <p className="mt-3 text-slate-700">
                Create your workspace, complete the full roadmap, use optional AI drafting, and export
                everything you produce. No payment details required. If we introduce paid plans later,
                we'll give you at least 30 days' notice, and everything you've created stays yours to keep.
              </p>
            </div>
            <ul className="grid gap-2.5 text-sm text-slate-700">
              {["Full workspace and roadmap", "AI drafting included", "All your business assets", "Export any time"].map((f) => (
                <li key={f} className="flex items-center gap-2"><Check size={16} className="text-brand" /> {f}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-6xl px-6 pb-16">
        <div className="overflow-hidden rounded-3xl bg-brand px-8 py-14 text-center shadow-lift lg:px-16">
          <h2 className="mx-auto max-w-2xl font-heading text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl">
            Create your free workspace and complete your first business asset today
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-brand-mutedHover">
            Do the first step now and finish with a real asset in hand.
          </p>
          <button
            data-testid="final-cta-btn"
            onClick={user ? goApp : startSignup}
            className="mt-8 inline-flex items-center gap-2 rounded-lg bg-white px-8 py-3.5 font-semibold text-brand transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lift"
          >
            {user ? "Open workspace" : "Create my free workspace"} <ArrowRight size={18} />
          </button>
          <p className="mt-4 text-sm text-brand-mutedHover">No credit card. Free during early access.</p>
        </div>
      </section>

      <footer className="border-t border-brand-line py-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-3 px-6 text-center">
          <Logo height="h-8" />
          <p className="text-sm text-slate-500">A calm workspace for building your first business.</p>
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-slate-400">
            <a href="/privacy" className="hover:text-slate-700">Privacy</a>
            <span>·</span>
            <a href="/privacy" className="hover:text-slate-700">AI &amp; data policy</a>
            <span>·</span>
            <a href="/terms" className="hover:text-slate-700">Terms</a>
            <span>·</span>
            <a href="/help" className="hover:text-slate-700">Help</a>
            <span>·</span>
            <a href="mailto:admin@launchtoincome.com" className="hover:text-slate-700">admin@launchtoincome.com</a>
          </div>
          <p className="mt-1 text-xs text-slate-400">Built and operated by AXON MEDIA LLC · Free during early access</p>
        </div>
      </footer>
    </div>
  );
}
